// The woods a sauna is clad in, in the 3D view (docs/claude/woods.md). Each is a
// photographed texture of boards, laid along the image's x, in
// src/renderer/woods/<id>.jpg (served at woods/<id>.jpg).

import type {
  CeilingSlats,
  RoomWoods,
  WallSlats,
} from './types';

/** A wood's key, as stored in a drawing: never renamed (its name can be). */
export type WoodId =
  | 'hemlock'
  | 'linden'
  | 'spruce'
  | 'spruce-black'
  | 'thermo-ash'
  | 'thermo-aspen';

export interface Wood {
  /** English: the DICT key its Italian and French names are looked up by. */
  name: string;
  /** Boards across the image's height, each `board` mm wide: the image spans
   *  boards × board mm tall, and as wide again as its pixels are. */
  boards: number;
  board: number;
  /** The image's size in pixels. */
  px: readonly [number, number];
  /** Its average colour: shown until the image loads, and where no image can be. */
  color: string;
}

/** Every wood, in the order the panel lists them (light to dark). */
export const WOODS: Readonly<Record<WoodId, Wood>> = {
  spruce: { name: 'Nordic spruce', boards: 25, board: 90, px: [1088, 1088], color: '#e9dac0' },
  linden: { name: 'Linden', boards: 25, board: 90, px: [1288, 1088], color: '#d0b69b' },
  hemlock: { name: 'Hemlock', boards: 25, board: 90, px: [1288, 1088], color: '#ceb195' },
  'thermo-aspen': { name: 'Thermoaspen', boards: 25, board: 90, px: [1113, 973], color: '#71533a' },
  'thermo-ash': { name: 'Thermo ash', boards: 20, board: 90, px: [1011, 1178], color: '#6a4b37' },
  'spruce-black': { name: 'Black spruce', boards: 25, board: 90, px: [1090, 1063], color: '#343233' },
};

export const WOOD_IDS = Object.keys(WOODS) as WoodId[];

/** Keys a wood was once stored under: a drawing saved with one still reads it. */
const RENAMED: Readonly<Record<string, WoodId>> = { aspen: 'linden' };

/** The wood a stored key names, or null (none, or one this build lacks). */
export function woodOf(id: string | undefined): (Wood & { id: WoodId }) | null {
  const key = id && Object.prototype.hasOwnProperty.call(RENAMED, id) ? RENAMED[id] : id;
  return key && Object.prototype.hasOwnProperty.call(WOODS, key) ? { ...WOODS[key as WoodId], id: key as WoodId } : null;
}

/** The size of a wood's image on a surface, in mm. */
export function woodImageMm(w: Wood): { w: number; h: number } {
  const h = w.boards * w.board;
  return { w: (h * w.px[0]) / w.px[1], h };
}

/** Where the app serves a wood's image, relative to its page. */
export function woodUrl(id: WoodId): string {
  return `woods/${id}.jpg`;
}

/** A room its woods clad: a walled sauna (a steam room is tiled). */
export function takesWood(inst: { autoBenches?: boolean; category: string }): boolean {
  return !!inst.autoBenches && inst.category === 'sauna';
}

const WALL_SLATS: readonly WallSlats[] = ['horizontal', 'vertical'];
const CEILING_SLATS: readonly CeilingSlats[] = ['lengthwise', 'crosswise'];

/** Which way a room's wall boards run, inside or out: the outside follows the
 *  inside while it has no wood of its own. Vertical by default. */
export function wallSlatsOf(woods: RoomWoods | undefined, side: 'in' | 'out'): WallSlats {
  const s = (side === 'out' && woodOf(woods?.wallsOut?.wood) ? woods?.wallsOut : woods?.wallsIn)?.slats;
  return s && WALL_SLATS.includes(s) ? s : 'vertical';
}

/** Which way a room's ceiling boards run: along its longest wall by default,
 *  and while the ceiling follows the walls. */
export function ceilingSlatsOf(woods: RoomWoods | undefined): CeilingSlats {
  const s = woodOf(woods?.ceiling?.wood) ? woods?.ceiling?.slats : undefined;
  return s && CEILING_SLATS.includes(s) ? s : 'lengthwise';
}

/** Clad a part of a room in a wood ('' takes it off), or turn its boards; a
 *  room left with no wood drops `woods`. An unknown wood, or a direction its
 *  part's boards cannot run, changes nothing. */
export function setRoomWood(inst: { woods?: RoomWoods }, part: keyof RoomWoods, change: { wood?: string; slats?: string }): void {
  const w: RoomWoods = { ...inst.woods };
  let wood = change.wood;
  if (wood === '') delete w[part];
  else if (wood !== undefined && woodOf(wood)) {
    wood = woodOf(wood)!.id;
    if (part === 'benches') w.benches = { wood };
    else if (part === 'ceiling') w.ceiling = { ...w.ceiling, wood };
    else w[part] = { ...w[part], wood };
  }
  const slats = change.slats;
  if (slats !== undefined) {
    if (part === 'ceiling') { if (w.ceiling && (CEILING_SLATS as readonly string[]).includes(slats)) w.ceiling = { ...w.ceiling, slats: slats as CeilingSlats }; }
    else if (part !== 'benches') { const f = w[part]; if (f && (WALL_SLATS as readonly string[]).includes(slats)) w[part] = { ...f, slats: slats as WallSlats }; }
  }
  if (Object.keys(w).length) inst.woods = w;
  else delete inst.woods;
}
